import React, { useState, useContext, useRef, useEffect } from "react";
import { Button, Card, Col, Form, InputGroup, Row, Table } from "react-bootstrap";
import { ReviewContext } from "../ReviewPatient";
import { ADD,BEDTIME,BREAKFAST,CANCEL,DINNER,DOSE,DOSE_REQUIRED,INSULIN,INSULIN_REQUIRED,LUNCH,METFORMIN,MORNING,PATIENT_MEDICATION_CREATE, SAVE } from "../../util/const";
import MedicationRegimeGroupDisplay from "../MedicationRegimeGroupDisplay";
import { MedicationRegimeGroup } from "../../types/patient";
import { DatabaseManager } from "../../services/DatabaseManager";


const Medications = () => {

  const context = useContext(ReviewContext);
  var patient = context.patient;

  const [isAdding, setIsAdding] = useState(false); // show/hide the add form
  const [group, setGroup] = useState({} as MedicationRegimeGroup); // for the copy feature
  const [groupLoaded, setGroupLoaded] = useState(false); // so we only run the useEffect once after group has been set
  const [count, setCount] = useState(0); // forces a refresh when any of the inputs change

  useEffect(() => {
  
    if(null == group || null == group.regimes || group.regimes.length === 0)
      return;
    
    if(!groupLoaded)
    {
      for (const regime of group.regimes)
      {
        if(null == regime.name || regime.name === undefined)
          continue;
  
        let isMetformin = regime.name.toLowerCase() === METFORMIN.toLowerCase();
        if(regime.morning !== "0")
        {
          if(isMetformin)
            adjustM.current.metforminDose = regime.morning;
          else
          {
            adjustM.current.insulinName = regime.name;
            adjustM.current.insulinDose = regime.morning;
          }
        }
        if(regime.breakfast !== "0")
        {
          if(isMetformin)
            adjustBB.current.metforminDose = regime.breakfast;
          else
          {
            adjustBB.current.insulinName = regime.name;
            adjustBB.current.insulinDose = regime.breakfast;
          }
        }
        if(regime.lunch !== "0")
        {
          if(isMetformin)
            adjustBL.current.metforminDose = regime.lunch;
          else
          {
            adjustBL.current.insulinName = regime.name;
            adjustBL.current.insulinDose = regime.lunch;
          }
        }
        if(regime.dinner !== "0")
        {
          if(isMetformin)
            adjustBD.current.metforminDose = regime.dinner;
          else
          {
            adjustBD.current.insulinName = regime.name;
            adjustBD.current.insulinDose = regime.dinner;
          }
        }
        if(regime.bedtime !== "0")
        {
          if(isMetformin)
            adjustB.current.metforminDose = regime.bedtime;
          else
          {
            adjustB.current.insulinName = regime.name;
            adjustB.current.insulinDose = regime.bedtime;
          }
        }
      }
      setGroupLoaded(true);
    }
  },[group, groupLoaded]);

  const DefaultMedicationDose = () => {
    return {
      insulinName:"", insulinDose:"", metforminDose: "", error: ""
    }
  }

  const adjustM = useRef(DefaultMedicationDose());
  const adjustBB = useRef(DefaultMedicationDose());
  const adjustBL = useRef(DefaultMedicationDose());
  const adjustBD = useRef(DefaultMedicationDose());
  const adjustB = useRef(DefaultMedicationDose());


  const resetForm =() => {
    adjustM.current = DefaultMedicationDose();
    adjustBB.current = DefaultMedicationDose();
    adjustBL.current = DefaultMedicationDose();
    adjustBD.current = DefaultMedicationDose();
    adjustB.current = DefaultMedicationDose();
    setGroup({} as MedicationRegimeGroup);
  }

  const addItem = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();
    resetForm();
    setIsAdding(!isAdding);
  };

  const cancelAddItem = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();
    resetForm();
    setGroupLoaded(false); // ensure the next call to copyItem will be successful
    setIsAdding(!isAdding);
  };

  const copyItem = (group: MedicationRegimeGroup) => {

    if(group === undefined || null == group)
      return;

    let regimes = group.regimes;
    if (null == regimes || regimes.length === 0)
      return;

    setGroup(group);
    setIsAdding(!isAdding);
  }

  const handleSave = () => {
    let medication = {
      "morning": {insulinName: adjustM.current.insulinName, insulinDose: adjustM.current.insulinDose, metforminDose: adjustM.current.metforminDose},
      "beforeBreakfast": {insulinName: adjustBB.current.insulinName, insulinDose: adjustBB.current.insulinDose, metforminDose: adjustBB.current.metforminDose},
      "beforeLunch": {insulinName: adjustBL.current.insulinName, insulinDose: adjustBL.current.insulinDose, metforminDose: adjustBL.current.metforminDose},
      "beforeDinner": {insulinName: adjustBD.current.insulinName, insulinDose: adjustBD.current.insulinDose, metforminDose: adjustBD.current.metforminDose},
      "bedtime": {insulinName: adjustB.current.insulinName, insulinDose: adjustB.current.insulinDose, metforminDose: adjustB.current.metforminDose}
    }
    
    /// testing
    //patient.documentId = "ft4pov7eZ0Fm0uG8DG11bRW8ejmS";
    /// end testing

    let medicationDoseAdjustment = {
      resourceId: patient.resourceId,
      documentId: patient.documentId,
      recordedOn: new Date().toISOString(),
      doses: medication
    }

    DatabaseManager.getInstance()
      .post(PATIENT_MEDICATION_CREATE, medicationDoseAdjustment)
      .then(() => {
        context.reload();
        setIsAdding(!isAdding);
      })
      .catch((error) => {
        console.error(error);
      })
  }
  
  const cannotAdd = () => {
    return patient.documentId == null || patient.documentId.length === 0
  }

  const noRegimes = () => {
    return null == patient.medicationRegimeGroups || patient.medicationRegimeGroups.length === 0;
  }

  const refForTod = (tod: string) => {
    if(tod === MORNING)
      return adjustM.current;
    else if(tod === BREAKFAST)
      return adjustBB.current;
    else if(tod === LUNCH)
      return adjustBL.current;
    else if(tod === DINNER)
      return adjustBD.current;
    else if(tod === BEDTIME)
      return adjustB.current;
    return null;
  }

  const handleInputChanged = (tod: string, key: string, value: string) => {
    const mutable = refForTod(tod);
    if(mutable != null)
    {
      if(key === METFORMIN) 
        mutable.metforminDose = value;
      else if(key === INSULIN)
        mutable.insulinName = value;
      else if(key === DOSE)
        mutable.insulinDose = value;

      //console.log("mutable.metforminDose " + mutable.metforminDose + " mutable.insulinName " + mutable.insulinName + " mutable.insulinDose " + mutable.insulinDose);

      if(mutable.insulinName === "" && mutable.insulinDose !== "") // name empty, dose not empty
        mutable.error = INSULIN_REQUIRED;
      else if(mutable.insulinDose === "" && mutable.insulinName !== "") // dose empty, name not empty
        mutable.error = DOSE_REQUIRED;
      else if((mutable.insulinDose === "" && mutable.insulinName === "") || (mutable.insulinDose !== "" && mutable.insulinName !== "")) // both empty or both not empty
        mutable.error = "";

      setCount(count + 1); /// refresh the page
    }
  }

  const insulinEditor = (value: string, tod: string) => {
    return <Form.Select 
      value={value}  
      onChange={(e) => {handleInputChanged(tod, INSULIN, e.target.value)}}>
      <option value=""></option>
      <option value="Novorapid">Novorapid</option>
      <option value="Protophane">Protophane</option>
      <option value="Degludec">Degludec</option>
      <option value="Humalog">Humalog</option>
      <option value="Levemir">Levemir</option>
      <option value="Optisulin">Optisulin</option>
    </Form.Select>
  }

  const doseEditor = (value: string, tod: string) => {
    return <InputGroup className="mb-3">
      <Form.Control type="text" 
        value={value} 
        onChange={(e) => {handleInputChanged(tod, DOSE, e.target.value)}}/>
      <InputGroup.Text id="basic-addon2">units</InputGroup.Text>
    </InputGroup>
  }

  const metforminEditor = (value: string, tod: string) => {
    return <InputGroup className="mb-3">
      <Form.Select 
        value={value}
        onChange={(e) => {handleInputChanged(tod, METFORMIN, e.target.value)}}>
        <option value=""></option>
        <option value="500">500</option>
        <option value="1000">1000</option>
        <option value="1500">1500</option>
        <option value="2000">2000</option>
      </Form.Select>
      <InputGroup.Text id="basic-addon2">mg</InputGroup.Text>
    </InputGroup> 
  }

  if(isAdding)
  {
    return (
      <Row className="mt-3 mb-3">
        <Col>
          <Card>
            <Card.Header className="fs-5">
              <Row>
                <Col></Col>
                <Col className="right">
                  <Button id="btn-add" type="button" className='fs-6 btn-color me-3' onClick={cancelAddItem}>{CANCEL}</Button>
                  <Button id="btn-save" type="button" className='fs-6 btn-color' onClick={handleSave}>{SAVE}</Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Form noValidate >
                <Row className="mt-3">
                  <Col sm={1}></Col>
                  <Col>Insulin Name</Col>
                  <Col>Insulin Dose</Col>
                  <Col>Metformin Dose</Col>
                  <Col sm={6}></Col>
                </Row>
                <Row className="mt-3">
                  <Col sm={1}>Morning:</Col>
                  <Form.Group as={Col} controlId={`morning-insulin`}>
                    {insulinEditor(adjustM.current.insulinName, MORNING)}
                  </Form.Group>
                  <Form.Group as={Col} controlId={`morning-dose`}>
                    {doseEditor(adjustM.current.insulinDose, MORNING)}
                  </Form.Group>
                  <Form.Group as={Col} controlId={`morning-metformin`}>
                    {metforminEditor(adjustM.current.metforminDose, MORNING)}
                  </Form.Group>
                  <Col sm={6} className="alert-red">{adjustM.current.error}</Col>
                </Row>
                <Row className="mt-3">
                  <Col sm={1}>Before Breakfast:</Col>
                  <Form.Group as={Col} controlId={`breakfast-insulin`}>
                    {insulinEditor(adjustBB.current.insulinName, BREAKFAST)}
                  </Form.Group>
                  <Form.Group as={Col} controlId={`breakfast-dose`}>
                    {doseEditor(adjustBB.current.insulinDose, BREAKFAST)}
                  </Form.Group>
                  <Form.Group as={Col} controlId={`breakfast-metformin`}>
                    {metforminEditor(adjustBB.current.metforminDose, BREAKFAST)}
                  </Form.Group>
                  <Col sm={6} className="alert-red">{adjustBB.current.error}</Col>
                </Row>
                <Row className="mt-3">
                  <Col sm={1}>Before Lunch:</Col>
                  <Form.Group as={Col} controlId={`lunch-insulin`}>
                    {insulinEditor(adjustBL.current.insulinName, LUNCH)}
                  </Form.Group>
                  <Form.Group as={Col} controlId={`lunch`}>
                    {doseEditor(adjustBL.current.insulinDose, LUNCH)}
                  </Form.Group>
                  <Form.Group as={Col} controlId={`lunch-metformin`}>
                    {metforminEditor(adjustBL.current.metforminDose, LUNCH)}
                  </Form.Group>
                  <Col sm={6} className="alert-red">{adjustBL.current.error}</Col>
                </Row>
                <Row className="mt-3">
                  <Col sm={1}>Before Dinner:</Col>
                  <Form.Group as={Col} controlId={`dinner-insulin`}>
                    {insulinEditor(adjustBD.current.insulinName, DINNER)}
                  </Form.Group>
                  <Form.Group as={Col} controlId={`dinner-dose`}>
                    {doseEditor(adjustBD.current.insulinDose, DINNER)}
                  </Form.Group>
                  <Form.Group as={Col} controlId={`dinner-metformin`}>
                    {metforminEditor(adjustBD.current.metforminDose, DINNER)}
                  </Form.Group>
                  <Col sm={6} className="alert-red">{adjustBD.current.error}</Col>
                </Row>
                <Row className="mt-3">
                  <Col sm={1}>Before Bed:</Col>
                  <Form.Group as={Col} controlId={`bedtime-insulin`}>
                    {insulinEditor(adjustB.current.insulinName, BEDTIME)}
                  </Form.Group>
                  <Form.Group as={Col} controlId={`bedtime-dose`}>
                    {doseEditor(adjustB.current.insulinDose, BEDTIME)}
                  </Form.Group>
                  <Form.Group as={Col} controlId={`bedtime-metformin`}>
                    {metforminEditor(adjustB.current.metforminDose, BEDTIME)}
                  </Form.Group>
                  <Col sm={6} className="alert-red">{adjustB.current.error}</Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="mt-3">
      <Col>
        <Table bordered hover striped className="review-table">
          <thead>
          <tr>
            <th>  
              <Row>
                <Col className="right me-2"><Button id="btn-add" type="button" className='fs-6 btn-color' disabled={cannotAdd()} onClick={addItem} >{ADD}</Button></Col>
              </Row>
            </th>
          </tr>
          <tr>
            <th className="section"></th>
          </tr>
          </thead>
          <tbody>
          {
            noRegimes() ?
              <tr><td className="pad-left-15 left">There are no recorded medication dose adjustments.</td></tr> :
            cannotAdd() ? 
              <tr><td className="pad-left-15 left">This patient is not linked. A medication dose adjustment can only be recorded for a linked patient.</td></tr> :
                patient.medicationRegimeGroups.map((element: MedicationRegimeGroup, i: number) => {
                return (
                  <React.Fragment key={i}>
                    <tr>
                      <td>
                        <MedicationRegimeGroupDisplay regimeGroup = {element} copyItem = {copyItem}/>
                      </td>
                    </tr>   
                  </React.Fragment>
                )
            })
          } 
          </tbody>
        </Table>
      </Col>
    </Row>
  );
};

export default Medications;